import { MonetaryAmount } from '@components'
import { VatCode } from '@constants'
import { IInvoiceRow } from '@query'
import { getInvoiceGrossTotal, getInvoiceRowGrossAmount } from '@pages/InvoicingPage/invoiceAmounts'
import { IVatCode } from '@root/query/constants'
import { useVATCodes } from '@root/query/hooks/useVATCodes'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  InvoiceLineItemAmount,
  InvoiceLineItemAmountMeta,
  InvoiceLineItemDescription,
  InvoiceLineItemHeader,
  InvoiceLineItemMobileMeta,
  InvoiceLineItemName,
  InvoiceLineItemNameMeta,
  InvoiceLineItemQuantity,
  InvoiceLineItemRow,
  InvoiceLineItemsTable as StyledInvoiceLineItemsTable,
  InvoiceLineItemTotal,
  InvoiceLineItemTotals,
  InvoiceLineItemTotalsInner,
  InvoiceLineItemTotalLabel,
  InvoiceLineItemTotalValue
} from './InvoicingDetails.styled'

interface Props {
  rows: IInvoiceRow[]
  totalAmount: number
  totalVatAmount: number
  showColor?: boolean
}

const VAT_CODES_WITHOUT_RATE = [VatCode.TAX_FREE, VatCode.DOMESTIC_ZERO_TAX_RATE]

const formatVatRate = (rate: number) => {
  const normalized = Number.isFinite(rate) ? rate : 0
  return `${normalized}%`
}

const formatVatTreatment = (
  vatCodes: IVatCode[] | undefined,
  vatCode: VatCode,
  vatRate: number
) => {
  const label = vatCodes?.find(code => code.value === vatCode)?.label
  const hideRate = VAT_CODES_WITHOUT_RATE.includes(vatCode)

  if (label && hideRate) return label
  if (label) return `${label} · ${formatVatRate(vatRate)}`
  return formatVatRate(vatRate)
}

export const InvoiceLineItems: React.FC<Props> = ({
  rows,
  totalAmount,
  totalVatAmount,
  showColor = false
}) => {
  const [t] = useTranslation()
  const { data: vatCodes } = useVATCodes()
  const grandTotal = getInvoiceGrossTotal(totalAmount, totalVatAmount)

  return (
    <StyledInvoiceLineItemsTable>
      <InvoiceLineItemHeader>
        <span>{t('invoicing.form.rows.quantityWithoutUnit')}</span>
        <span>{t('invoicing.form.rows.description')}</span>
        <span style={{ textAlign: 'right' }}>{t('invoicing.form.rows.amount')}</span>
      </InvoiceLineItemHeader>

      {rows.map(row => {
        const quantityLabel = `${row.product_count} ${row.unit}`
        const vatTreatment = formatVatTreatment(vatCodes, row.vat_code, row.vat_rate)

        return (
          <InvoiceLineItemRow key={`invoice-row-${row.id}`}>
            <InvoiceLineItemQuantity data-test="invoice-details-quantity">
              {quantityLabel}
            </InvoiceLineItemQuantity>
            <InvoiceLineItemDescription>
              <InvoiceLineItemName data-test="invoice-details-name">{row.name}</InvoiceLineItemName>
              <InvoiceLineItemNameMeta>{vatTreatment}</InvoiceLineItemNameMeta>
            </InvoiceLineItemDescription>
            <InvoiceLineItemMobileMeta>
              {quantityLabel} · {vatTreatment}
            </InvoiceLineItemMobileMeta>
            <InvoiceLineItemAmount data-test="invoice-details-amount">
              <MonetaryAmount value={getInvoiceRowGrossAmount(row)} showColor={showColor} />
              <InvoiceLineItemAmountMeta>
                {t('invoicing.form.rows.vatShare')}{' '}
                <MonetaryAmount value={row.total_vat_amount} showColor={showColor} />
              </InvoiceLineItemAmountMeta>
            </InvoiceLineItemAmount>
          </InvoiceLineItemRow>
        )
      })}

      <InvoiceLineItemTotals data-test="invoice-details-total-amount">
        <InvoiceLineItemTotalsInner>
          <InvoiceLineItemTotal>
            <InvoiceLineItemTotalLabel>{t('invoicing.form.totals.net')}</InvoiceLineItemTotalLabel>
            <InvoiceLineItemTotalValue>
              <MonetaryAmount value={totalAmount} showColor={showColor} />
            </InvoiceLineItemTotalValue>
          </InvoiceLineItemTotal>
          <InvoiceLineItemTotal>
            <InvoiceLineItemTotalLabel>{t('invoicing.form.totals.vat')}</InvoiceLineItemTotalLabel>
            <InvoiceLineItemTotalValue>
              <MonetaryAmount value={totalVatAmount} showColor={showColor} />
            </InvoiceLineItemTotalValue>
          </InvoiceLineItemTotal>
          <InvoiceLineItemTotal $emphasis>
            <InvoiceLineItemTotalLabel $emphasis>
              {t('invoicing.form.totals.sum')}
            </InvoiceLineItemTotalLabel>
            <InvoiceLineItemTotalValue $emphasis>
              <MonetaryAmount value={grandTotal} showColor={showColor} />
            </InvoiceLineItemTotalValue>
          </InvoiceLineItemTotal>
        </InvoiceLineItemTotalsInner>
      </InvoiceLineItemTotals>
    </StyledInvoiceLineItemsTable>
  )
}
